import type { FilterLayout } from '../filter-operators.ts';
import { drilldownFilterValues, drilldownsFor, type Drilldown } from './drilldown.ts';
import type { BuildFilterConfig, UserFilterConfig, WidgetConfig } from './widget-config-types.ts';

export interface WidgetFilterInputs {
  /** Dashboard-level build filters; the page passes none on a multi-dataset dashboard. */
  buildFilters?: BuildFilterConfig;
  /** Dashboard-level viewer filters, same single-dataset rule. */
  userFilters?: UserFilterConfig;
  /** Every active drilldown; only those on this widget's dataset from OTHER widgets apply. */
  drilldowns?: Drilldown[];
}

export interface EffectiveWidgetFilters {
  buildFilterValues: Record<string, unknown>;
  buildFilterLayout: FilterLayout;
  userFilterValues: Record<string, unknown>;
  /** Drilldowns stay out of the user map so a same-field drilldown ANDs instead of overwriting. */
  extraFilterValues: Record<string, unknown>;
}

/**
 * The one merge of everything that filters a widget's query, shared by the live
 * renderer and the CSV export so the two cannot drift.
 *
 * @param inputs dashboard-level filters and drilldowns; the widget's own `buildFilter` is read from `widget`
 * @returns build layouts concatenated (each slot AND'd, OR within its group) with the
 *   widget's value winning a same-field collision, the viewer values as-is, and the
 *   drilldowns that reach this widget encoded as flat filter entries
 */
export function effectiveWidgetFilters(
  widget: WidgetConfig,
  inputs: WidgetFilterInputs = {}
): EffectiveWidgetFilters {
  const widgetBuild = widget.buildFilter;
  return {
    buildFilterValues: {
      ...(inputs.buildFilters?.filterValues ?? {}),
      ...(widgetBuild?.filterValues ?? {}),
    },
    buildFilterLayout: [
      ...(inputs.buildFilters?.filterLayout ?? []),
      ...(widgetBuild?.filterLayout ?? []),
    ],
    userFilterValues: inputs.userFilters?.filterValues ?? {},
    extraFilterValues: drilldownFilterValues(drilldownsFor(inputs.drilldowns, widget)),
  };
}
